import { BrowserRouter as Router } from 'react-router-dom';

import { PrimeReactProvider } from 'primereact/api';

import ConfigProvider from 'lib/core/config';
import AuthProvider from 'lib/core/context/AuthProvider';
import StreamsProvider from 'lib/core/context/StreamsProvider';

import AgentProvider from 'lib/common/contexts/AgentContext';
import AgentPreferencesProvider from 'lib/common/contexts/AgentPreferenceContext';
import EmailUploadProvider from 'lib/common/contexts/EmailUploadContext';
import InstanceProvider from 'lib/common/contexts/InstanceContext';
import LogStoreProvider from 'lib/common/contexts/LogStoreContext';
import ModulesProvider from 'lib/common/contexts/ModulesContext';
import OverlayProvider from 'lib/common/contexts/OverlayContext';
import PreferencesProvider from 'lib/common/contexts/PreferencesContext';
import RequirementsProvider from 'lib/common/contexts/RequirementsContext';
import DesktopLayoutProvider from 'lib/common/contexts/layout/DesktopLayoutContext';

import DynamicPageTitle from 'lib/common/components/DynamicPageTitle';
import LoggerController from 'lib/common/components/LoggerController/LoggerController';
import Offline from 'lib/common/components/Offline';
import ToastContainer from 'lib/common/components/ToastContainer';

import 'lib/common/customIcons';
import isIsolatedMode from 'lib/common/utils/isIsolatedMode';
import { IsolatedRoutes, Routes } from 'lib/routes';

import ErrorBoundary from './ErrorBoundary';

function IsolatedApp() {
  return (
    <AuthProvider isIsolatedMode>
      <DynamicPageTitle />
      <IsolatedRoutes />
      <ToastContainer />
    </AuthProvider>
  );
}

function FullApp() {
  return (
    <PreferencesProvider>
      <StreamsProvider>
        <LogStoreProvider>
          <LoggerController>
            <AgentProvider>
              <AuthProvider>
                <AgentPreferencesProvider>
                  <ModulesProvider>
                    <InstanceProvider>
                      <RequirementsProvider>
                        <EmailUploadProvider>
                          <DesktopLayoutProvider>
                            <OverlayProvider>
                              <PrimeReactProvider value={{ unstyled: false }}>
                                <DynamicPageTitle />
                                <ErrorBoundary>
                                  <Routes />
                                </ErrorBoundary>
                              </PrimeReactProvider>
                            </OverlayProvider>
                          </DesktopLayoutProvider>
                        </EmailUploadProvider>
                      </RequirementsProvider>
                    </InstanceProvider>
                  </ModulesProvider>
                </AgentPreferencesProvider>
              </AuthProvider>
            </AgentProvider>
          </LoggerController>
        </LogStoreProvider>
      </StreamsProvider>
    </PreferencesProvider>
  );
}

export default function App() {
  const isIsolatedApp = isIsolatedMode();
  const children = isIsolatedApp ? <IsolatedApp /> : <FullApp />;

  return (
    <ErrorBoundary>
      <Router>
        <Offline>
          <ConfigProvider>{children}</ConfigProvider>
        </Offline>
      </Router>
    </ErrorBoundary>
  );
}
